import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./CheckweighingScales.css";
import { useProducts, type CheckweighingSpecs } from '../../api/products/products-api';
import { useAuth } from '../../api/auth/AuthContext';
import DbSection from '../../components/DbSection';
import ProductCard, { ProductCardSkeleton } from '../../components/ProductCard';

const CheckweighingScales: React.FC = () => {
  const { authType } = useAuth();
  const location = useLocation();
  const [editMode, setEditMode] = useState<boolean>(!!(location.state as any)?.editMode);
  const { products: models, loading, error } = useProducts<CheckweighingSpecs>('checkweighing-scales', editMode);
  const navigate = useNavigate();

  const optionalConfigurations = [
    "Tarjetas USB/inalámbricas",
    "Cajas de luz externas",
    "Paquetes de batería",
    "Opciones de soporte variables",
    "Conectividad Ethernet",
    "Software de gestión de datos",
  ];

  const applications = [
    {
      title: "Procesamiento de Alimentos",
      description: "Verificación de peso en líneas de empaque y producción alimentaria.",
    },
    {
      title: "Control de Calidad",
      description: "Clasificación automática de productos por peso en manufactura.",
    },
    {
      title: "Industria Farmacéutica",
      description: "Verificación precisa de peso en productos farmacéuticos.",
    },
    {
      title: "Logística",
      description: "Control de peso de paquetes en centros de distribución.",
    },
    {
      title: "Agricultura",
      description: "Clasificación de productos agrícolas por peso y calidad.",
    },
    {
      title: "Industria Química",
      description: "Verificación de peso en procesos químicos e industriales.",
    },
  ];

  return (
    <div className="cw-page">
      {error && <div className="cw-status-bar cw-status-bar--error"><p>Error al cargar productos. Por favor intente de nuevo.</p></div>}

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="cw-hero">
        <div className="container cw-hero-content">
          <nav className="cw-breadcrumb" aria-label="Navegación de ruta">
            <Link to="/">Inicio</Link>
            <span className="cw-breadcrumb-sep" aria-hidden="true">/</span>
            <Link to="/productos">Productos</Link>
            <span className="cw-breadcrumb-sep" aria-hidden="true">/</span>
            <span>Básculas Verificadoras</span>
          </nav>
          <h1 className="cw-hero-title">Básculas Verificadoras</h1>
          <p className="cw-hero-subtitle">
            Construidas en acero inoxidable grado alimentario para soportar
            manejo rudo, lavado frecuente y los ambientes industriales más exigentes.
          </p>
          {authType === 'internal' && (
            <button
              className={`cw-btn-edit-mode${editMode ? ' cw-btn-edit-mode--active' : ''}`}
              onClick={() => setEditMode(m => !m)}
            >
              {editMode ? 'Salir de edición' : 'Modo edición'}
            </button>
          )}
        </div>
        <div className="cw-hero-image" aria-hidden="true">
          <img src="/images/checkweigher-scales.jpg" alt="" loading="lazy" />
        </div>
      </section>

      {/* ── Overview ─────────────────────────────────────────── */}
      <section className="cw-overview">
        <div className="container">
          <div className="cw-overview-grid">
            <div className="cw-overview-text">
              <span className="cw-label">Descripción general</span>
              <h2>Precisión en cada verificación</h2>
              <p>
                Las básculas verificadoras están diseñadas en acero inoxidable resistente
                (grado alimentario), permitiéndoles sobrevivir al manejo rudo frecuente y
                al lavado. Son ideales para aplicaciones de control de calidad, clasificación
                automática y verificación de peso en líneas de producción, con certificación
                NSF/ANSI Standard 3-A y protección IP65/IP69K contra agua y polvo.
              </p>
            </div>
            <div className="cw-overview-image">
              
            </div>
          </div>
        </div>
      </section>

      {/* ── Products ─────────────────────────────────────────── */}
      <section className="cw-products">
        <div className="container">
          <DbSection>
            <div className="catalog-header">
              <h2 className="catalog-title">Modelos Disponibles</h2>
              {authType === 'internal' && editMode && (
                <button className="btn-add-product" onClick={() => navigate('/productos/nuevo/checkweighing-scales')}>
                  + Agregar producto
                </button>
              )}
            </div>
            <div className="models-grid">
              {loading
                ? Array.from({ length: 3 }, (_, i) => <ProductCardSkeleton key={i} />)
                : models.map((model, index) => (
                <ProductCard
                  key={index}
                  reviewed={model.reviewed}
                  endOfLife={model.endOfLife}
                  productId={model.productId}
                  name={model.name}
                  badge={model.specs?.baseType ?? undefined}
                  description={model.specs?.description ?? undefined}
                  image={model.image}
                  onRevisar={editMode && model.productId ? (id) => navigate(`/productos/revisar/${encodeURIComponent(id)}`, { state: { editMode: true, from: location.pathname } }) : undefined}
                  editMode={editMode}
                  sections={[
                    ...(model.specs?.capacities?.length    ? [{ icon: '📦', label: 'Capacidades',     items: (model.specs?.capacities   ?? []).filter((x): x is string => x != null) }] : []),
                    ...(model.specs?.platformSizes?.length ? [{ icon: '📐', label: 'Plataformas',     items: (model.specs?.platformSizes ?? []).filter((x): x is string => x != null) }] : []),
                    ...(model.specs?.protection?.length    ? [{ icon: '🛡️', label: 'Protección',      items: (model.specs?.protection   ?? []).filter((x): x is string => x != null) }] : []),
                    ...(model.specs?.power?.length         ? [{ icon: '⚡', label: 'Alimentación',    items: (model.specs?.power        ?? []).filter((x): x is string => x != null) }] : []),
                    ...(model.specs?.features?.length      ? [{ icon: '⚙️', label: 'Características', items: (model.specs?.features     ?? []).filter((x): x is string => x != null) }] : []),
                  ]}
                />
              ))}
            </div>
          </DbSection>
        </div>
      </section>

      {/* ── Color coding ─────────────────────────────────────── */}
      <section className="cw-color-coding">
        <div className="container">
          <span className="cw-label">Sistema de verificación</span>
          <h2 className="cw-section-title">Códigos de color</h2>
          <p className="cw-section-intro">
            El display integrado clasifica cada producto de forma visual e instantánea
            para agilizar el control de calidad en línea.
          </p>
          <div className="cw-color-grid">
            <div className="cw-color-card">
              <span className="cw-color-dot cw-color-dot--red" aria-hidden="true"></span>
              <div>
                <h3>Bajo peso</h3>
                <p>Producto por debajo del rango objetivo</p>
              </div>
            </div>
            <div className="cw-color-card">
              <span className="cw-color-dot cw-color-dot--green" aria-hidden="true"></span>
              <div>
                <h3>Peso correcto</h3>
                <p>Producto dentro del rango aceptado</p>
              </div>
            </div>
            <div className="cw-color-card">
              <span className="cw-color-dot cw-color-dot--yellow" aria-hidden="true"></span>
              <div>
                <h3>Sobrepeso</h3>
                <p>Producto por encima del rango objetivo</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Applications ─────────────────────────────────────── */}
      <section className="cw-applications">
        <div className="container">
          <span className="cw-label cw-label--light">Industrias</span>
          <h2 className="cw-section-title cw-section-title--light">Aplicaciones</h2>
          <div className="cw-applications-grid">
            {applications.map((app, index) => (
              <div key={index} className="cw-app-card">
                <h3>{app.title}</h3>
                <p>{app.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Optional configurations ───────────────────────────── */}
      <section className="cw-configurations">
        <div className="container">
          <div className="cw-configurations-inner">
            <div className="cw-configurations-header">
              <span className="cw-label">Personalización</span>
              <h2 className="cw-section-title">Configuraciones opcionales</h2>
              <p>
                Adapte su báscula verificadora a los requerimientos específicos
                de su proceso productivo.
              </p>
            </div>
            <ul className="cw-config-list" aria-label="Configuraciones opcionales">
              {optionalConfigurations.map((config, index) => (
                <li key={index} className="cw-config-item">
                  <svg className="cw-check-icon" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="10" cy="10" r="8.5" stroke="currentColor" strokeWidth="1.5"/>
                    <path d="M7 10l2.5 2.5L13 7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  {config}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CheckweighingScales;
